import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Divider,
  Alert,
  Box,
  Tooltip,
  IconButton,
  Skeleton,
  Chip,
  Switch,
  FormControlLabel,
} from "@mui/material";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ShareIcon from "@mui/icons-material/Share";
import StorefrontIcon from "@mui/icons-material/Storefront";
import CloudIcon from "@mui/icons-material/Cloud";
import CheckIcon from "@mui/icons-material/Check";
import LinkIcon from "@mui/icons-material/Link";

import { useAuthStore } from "../../store/authStore";
import { retailPosApi } from "./services/retailPosApi";
import { resolveRetailStoreName, slugifyForInput, SLUG_PATTERN } from "./utils/storeDisplayName";
import {
  getPhotosUploadStatus,
  isPhotosUploadActive,
  formatExpiryDate,
  getPhotosAccessFromPolicy,
} from "./utils/retailPremiumStatus";
import RetailPhotosStorageBar from "../kadskljasdljkdas/RetailPhotosStorageBar";

const DEFAULT_LIMIT_BYTES = 500 * 1024 * 1024;

function StorageUsageSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    retailPosApi
      .getStorageUsage()
      .then((res) => {
        const access = getPhotosAccessFromPolicy(res);
        setData({
          totalBytes: Number(res?.totalBytes ?? 0),
          fileCount: Number(res?.fileCount ?? 0),
          limitBytes:
            Number(res?.limitBytes) > 0 ? Number(res.limitBytes) : DEFAULT_LIMIT_BYTES,
          ...access,
        });
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const photosAccess = data
    ? {
        imagenesUpload: data.imagenesUpload,
        imagenesUploadExpiresAt: data.imagenesUploadExpiresAt,
      }
    : null;

  const photosStatus = photosAccess ? getPhotosUploadStatus(photosAccess) : null;
  const photosActive = photosAccess ? isPhotosUploadActive(photosAccess) : false;
  const expiryLabel = data?.imagenesUploadExpiresAt
    ? formatExpiryDate(data.imagenesUploadExpiresAt)
    : null;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} mb={1}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <CloudIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2" fontWeight={700}>
            Premium — fotos de producto (S3)
          </Typography>
        </Stack>
        {!loading && !error && photosStatus && (
          <Chip
            label={photosStatus.label}
            size="small"
            color={photosStatus.color}
            sx={{ fontWeight: 600 }}
          />
        )}
      </Stack>

      {loading ? (
        <>
          <Skeleton variant="rounded" height={56} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" height={40} />
        </>
      ) : error ? (
        <Alert severity="warning" sx={{ py: 0.5 }}>
          No se pudo obtener el estado de fotos.
        </Alert>
      ) : !data?.imagenesUpload ? (
        <Alert severity="info" sx={{ py: 0.5 }}>
          La carga de fotos es premium. Solicítala al administrador para activarla.
        </Alert>
      ) : (
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack spacing={0.75} sx={{ mb: 1.5 }}>
            <Typography variant="body2">
              <strong>Permitir subir fotos:</strong>{" "}
              {photosActive ? "Sí (activo)" : "No (vencido o pendiente)"}
            </Typography>
            {expiryLabel && (
              <Typography variant="body2" color="text.secondary">
                <strong>Vence fotos:</strong> {expiryLabel}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              <strong>Límite:</strong> {data.storageLimitMb} MB
            </Typography>
          </Stack>

          {!photosActive && (
            <Alert severity="warning" sx={{ py: 0.5, mb: 1.5 }}>
              El premium de fotos no está vigente. Renueva la fecha con el administrador.
            </Alert>
          )}

          <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={0.75}>
            Uso actual en S3
          </Typography>
          <RetailPhotosStorageBar
            totalBytes={data.totalBytes}
            fileCount={data.fileCount}
            storageLimitMb={data.storageLimitMb}
            loading={false}
          />
        </Box>
      )}
    </Box>
  );
}

export default function ConfigTienda({
  store,
  activeStore,
  resolvedStoreName,
  open,
  onClose,
}) {
  const fetchMyStores = useAuthStore((s) => s.fetchMyStores);

  const displayName = useMemo(
    () => resolvedStoreName || resolveRetailStoreName(store?.config, activeStore),
    [resolvedStoreName, store?.config, activeStore]
  );

  const [form, setForm] = useState({
    nombreNegocio: "",
    slug: "",
    moneda: "BOB",
    stockMinimo: 3,
    catalogoMostrarCodigoBarras: false,
  });
  const [loadingTienda, setLoadingTienda] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [slugError, setSlugError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const catalogUrl = form.slug
    ? `${window.location.origin}/store/${form.slug}`
    : null;

  const loadTienda = useCallback(async () => {
    setLoadingTienda(true);
    setLoadError("");
    try {
      const tienda = await retailPosApi.getTiendaActual();
      setForm({
        nombreNegocio:
          String(tienda?.nombreNegocio || "").trim() || displayName,
        slug: tienda?.slug || "",
        moneda: store.config?.moneda || "BOB",
        stockMinimo: store.config?.stockMinimo ?? 3,
        catalogoMostrarCodigoBarras: store.config?.catalogoMostrarCodigoBarras === true,
      });
      setSlugError("");
    } catch {
      setLoadError("No se pudo cargar los datos de la tienda.");
      setForm({
        nombreNegocio: displayName,
        slug: "",
        moneda: store.config?.moneda || "BOB",
        stockMinimo: store.config?.stockMinimo ?? 3,
        catalogoMostrarCodigoBarras: store.config?.catalogoMostrarCodigoBarras === true,
      });
    } finally {
      setLoadingTienda(false);
    }
  }, [
    displayName,
    store.config?.moneda,
    store.config?.stockMinimo,
    store.config?.catalogoMostrarCodigoBarras,
  ]);

  useEffect(() => {
    if (open) {
      setSaved(false);
      setSaveError("");
      loadTienda();
    }
  }, [open, loadTienda]);

  const handleSlugChange = (value) => {
    const formatted = slugifyForInput(value);
    setForm((prev) => ({ ...prev, slug: formatted }));
    if (!formatted) {
      setSlugError("El slug es obligatorio para el catálogo público.");
    } else if (!SLUG_PATTERN.test(formatted)) {
      setSlugError("Usa 3–50 caracteres: minúsculas, números y guiones.");
    } else {
      setSlugError("");
    }
  };

  const handleGuardar = async () => {
    setSaveError("");
    const nombre = String(form.nombreNegocio).trim();
    const slug = String(form.slug).trim();

    if (!nombre) {
      setSaveError("El nombre del negocio es obligatorio.");
      return;
    }
    if (!slug || !SLUG_PATTERN.test(slug)) {
      setSlugError("Indica un slug válido y único para tu catálogo.");
      return;
    }

    setSaving(true);
    try {
      await retailPosApi.updateTiendaActual({ nombreNegocio: nombre, slug });

      await store.actualizarConfig({
        nombreNegocio: nombre,
        moneda: form.moneda,
        stockMinimo: parseInt(form.stockMinimo, 10) || 3,
        catalogoMostrarCodigoBarras: form.catalogoMostrarCodigoBarras === true,
      });

      await fetchMyStores?.();

      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        onClose();
      }, 1200);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.msg ||
        "No se pudo guardar la configuración.";
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    if (!catalogUrl) return;
    try {
      await navigator.clipboard.writeText(catalogUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const handleShare = async () => {
    if (!catalogUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: form.nombreNegocio, url: catalogUrl });
      } catch {
        /* dismissed */
      }
    } else {
      handleCopy();
    }
  };

  const handleResetData = () => {
    if (
      window.confirm(
        "⚠️ ¿Estás seguro? Esto eliminará TODOS los datos (inventario, ventas, compras). Esta acción es irreversible."
      )
    ) {
      localStorage.removeItem("retail_pos_v1");
      window.location.reload();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        Configuración
        {displayName && (
          <Typography variant="caption" color="text.secondary" display="block">
            Tienda: {displayName}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {saved && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Configuración guardada
          </Alert>
        )}
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        )}
        {loadError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        )}

        {loadingTienda ? (
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Skeleton variant="rounded" height={56} />
            <Skeleton variant="rounded" height={56} />
            <Skeleton variant="rounded" height={56} />
          </Stack>
        ) : (
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Nombre del negocio"
              value={form.nombreNegocio}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, nombreNegocio: e.target.value }))
              }
              fullWidth
              helperText="Nombre visible en el POS y en el catálogo público"
            />

            <TextField
              label="Slug del catálogo (único)"
              value={form.slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              fullWidth
              error={Boolean(slugError)}
              helperText={
                slugError ||
                "URL pública: /store/tu-slug — solo minúsculas, números y guiones"
              }
              InputProps={{
                startAdornment: (
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                    /store/
                  </Typography>
                ),
              }}
            />

            <Box>
              <Stack direction="row" alignItems="center" spacing={0.75} mb={0.75}>
                <StorefrontIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" fontWeight={700}>
                  Catálogo público
                </Typography>
              </Stack>
              {catalogUrl && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.75,
                    bgcolor: "grey.50",
                    mb: 1.25,
                  }}
                >
                  <LinkIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: "0.78rem",
                      color: "primary.main",
                    }}
                  >
                    {catalogUrl}
                  </Typography>
                  <Tooltip title={copied ? "¡Copiado!" : "Copiar"}>
                    <IconButton size="small" onClick={handleCopy}>
                      {copied ? (
                        <CheckIcon fontSize="small" color="success" />
                      ) : (
                        <ContentCopyIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Abrir">
                    <IconButton
                      size="small"
                      component="a"
                      href={catalogUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Compartir">
                    <IconButton size="small" onClick={handleShare}>
                      <ShareIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
              <FormControlLabel
                sx={{ ml: 0, alignItems: "flex-start" }}
                control={
                  <Switch
                    size="small"
                    checked={form.catalogoMostrarCodigoBarras}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        catalogoMostrarCodigoBarras: e.target.checked,
                      }))
                    }
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Mostrar código de barras en el catálogo
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Desactivado por defecto. Los clientes solo lo ven si activas esta opción.
                    </Typography>
                  </Box>
                }
              />
            </Box>

            <FormControl fullWidth>
              <InputLabel>Moneda</InputLabel>
              <Select
                value={form.moneda}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, moneda: e.target.value }))
                }
                label="Moneda"
              >
                <MenuItem value="BOB">BOB (Boliviano)</MenuItem>
                <MenuItem value="USD">USD (Dólar)</MenuItem>
                <MenuItem value="ARS">ARS (Peso Argentino)</MenuItem>
                <MenuItem value="MXN">MXN (Peso Mexicano)</MenuItem>
                <MenuItem value="COP">COP (Peso Colombiano)</MenuItem>
                <MenuItem value="PEN">PEN (Sol Peruano)</MenuItem>
                <MenuItem value="CLP">CLP (Peso Chileno)</MenuItem>
                <MenuItem value="BRL">BRL (Real Brasileño)</MenuItem>
                <MenuItem value="EUR">EUR (Euro)</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Alerta de stock mínimo"
              value={form.stockMinimo}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, stockMinimo: e.target.value }))
              }
              type="number"
              fullWidth
              helperText="Productos con stock igual o menor aparecerán en alertas"
              inputProps={{ min: 0 }}
            />

            <Divider />
            <StorageUsageSection />
            <Divider />

            <Typography variant="subtitle2" color="text.secondary">
              Zona de peligro
            </Typography>
            <Button
              variant="outlined"
              color="error"
              onClick={handleResetData}
              size="small"
            >
              Borrar todos los datos
            </Button>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleGuardar} disabled={saving || loadingTienda}>
          {saving ? "Guardando…" : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
